import React from 'react';
import { usePayslipList } from './hooks/usePayslipList';
import SharedFilters from '../../../components/SharedFilters';
import AppPagination from '../../../components/AppPagination';
import PayslipSummaryCards from './components/PayslipSummaryCards';
import PayslipTable from './components/PayslipTable';
import PayslipDetailModal from './components/PayslipDetailModal';

export default function PayslipListScreen() {
  const { state, actions } = usePayslipList();

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Header Banner */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/15 pb-4">
        <div>
          <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
            Pay Slip
          </h1>
          <p className="mt-1.5 text-xs text-on-surface-variant font-medium">
            Monthly salary statement, pay heads breakdown and earning/deduction details
          </p>
        </div>

        {/* View Formal Payslip Button */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={actions.handleOpenSlipModal}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-xs font-bold text-on-primary shadow-xs hover:bg-primary/90 transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">receipt_long</span>
            <span>View Full Pay Slip</span>
          </button>
        </div>
      </section>

      {/* Summary KPI Cards */}
      <PayslipSummaryCards
        summary={state.summary}
        month={state.monthFilter}
        year={state.yearFilter}
      />

      {/* Shared Filters Component */}
      <SharedFilters
        searchField={state.searchField}
        searchText={state.searchText}
        showStatus={false}

        monthFilter={state.monthFilter}
        yearFilter={state.yearFilter}
        monthOptions={state.monthOptions}
        yearOptions={state.yearOptions}

        setSearchField={actions.setSearchField}
        setSearchText={actions.setSearchText}
        setMonthFilter={actions.setMonthFilter}
        setYearFilter={actions.setYearFilter}

        onSearch={actions.handleSearch}
        onReset={actions.resetFilters}

        searchFieldOptions={state.searchFieldOptions}

        visibleAccordionFilters={[
          'month',
          'year',
        ]}

        tools={state.tools}
        onToolSelect={actions.handleToolSelect}
      />

      {/* Payslip Table */}
      <section className="space-y-4">
        <PayslipTable
          records={state.records}
          isLoading={state.isLoading}
          onResetFilters={actions.resetFilters}
          onOpenSlipModal={actions.handleOpenSlipModal}
        />

        {/* Table Footer with Pagination */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row bg-surface-container-lowest rounded-2xl shadow-xs">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing {state.totalCount === 0 ? 0 : (state.page - 1) * state.pageSize + 1} to{' '}
            {Math.min(state.page * state.pageSize, state.totalCount)} of {state.totalCount} entries
          </p>
          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.setPage}
            onPrevious={() => actions.setPage((p) => Math.max(p - 1, 1))}
            onNext={() => actions.setPage((p) => Math.min(p + 1, state.totalPages))}
            canPrevious={state.page > 1}
            canNext={state.page < state.totalPages}
          />
        </div>
      </section>

      {/* Printable Pay Slip Statement Modal */}
      <PayslipDetailModal
        isOpen={state.isDetailModalOpen}
        onClose={actions.handleCloseSlipModal}
        records={state.allFilteredRecords}
        summary={state.summary}
        employeeInfo={state.employeeInfo}
        month={state.monthFilter}
        year={state.yearFilter}
      />
    </div>
  );
}
